import { useSigma } from '@react-sigma/core';
import Graph from 'graphology';
import FA2Layout from 'graphology-layout-forceatlas2/worker';
import React, {
  createContext,
  FC,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react';
import type Sigma from 'sigma';

/**
 * GraphInstanceContext - Graph and Sigma Instance Management
 *
 * This context manages the low-level graph instances that power the visualization:
 * - Sigma: The rendering engine
 * - Graph: Graphology graph instance
 * - Layout: Force-directed layout algorithm
 *
 * These instances are shared across the component tree to coordinate rendering,
 * layout calculations, and graph manipulations.
 */

interface GraphInstanceContextValue {
  /** Sigma rendering instance */
  sigma: Sigma | null;

  /** Graphology graph instance */
  graph: Graph | null;

  /** Force-directed layout instance */
  layout: FA2Layout | null;

  /** Update graph instance */
  setGraph: (graph: Graph | null) => void;

  /** Update layout instance */
  setLayout: (layout: FA2Layout | null) => void;
}

const GraphInstanceContext = createContext<GraphInstanceContextValue | null>(
  null
);

export interface GraphInstanceProviderProps {
  children: ReactNode;
}

/**
 * GraphInstanceProvider - Provides graph rendering instances
 *
 * This provider manages the core instances needed for graph visualization:
 *
 * 1. Sigma: The WebGL-based rendering engine from @react-sigma/core
 * 2. Graph: Graphology graph data structure
 * 3. Layout: Force-directed layout algorithm (FA2Layout)
 *
 * These instances are shared across components that need direct access to
 * graph manipulation and rendering APIs.
 *
 * Usage:
 * ```tsx
 * <SigmaContainer>
 *   <GraphInstanceProvider>
 *     <GraphComponents />
 *   </GraphInstanceProvider>
 * </SigmaContainer>
 * ```
 *
 * Access instances via:
 * ```tsx
 * const { sigma, graph, layout } = useGraphInstanceContext();
 *
 * // Manipulate graph
 * graph?.addNode('node1', { x: 0, y: 0, label: 'Node 1' });
 *
 * // Trigger re-render
 * sigma?.refresh();
 *
 * // Control layout
 * layout?.start();
 * ```
 *
 * Note: This provider must be wrapped in a SigmaContainer from @react-sigma/core
 * to have access to the sigma instance.
 */
export const GraphInstanceProvider: FC<GraphInstanceProviderProps> = ({
  children,
}) => {
  // Get Sigma instance from @react-sigma/core context
  const sigma = useSigma();

  // Local state for graph and layout instances
  const [graph, setGraph] = useState<Graph | null>(null);
  const [layout, setLayout] = useState<FA2Layout | null>(null);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo<GraphInstanceContextValue>(
    () => ({
      sigma,
      graph,
      layout,
      setGraph,
      setLayout,
    }),
    [sigma, graph, layout]
  );

  return (
    <GraphInstanceContext.Provider value={value}>
      {children}
    </GraphInstanceContext.Provider>
  );
};

/**
 * useGraphInstanceContext - Access graph rendering instances
 *
 * This hook provides access to the core graph instances needed for
 * visualization and manipulation. Must be used within a GraphInstanceProvider.
 *
 * @throws Error if used outside GraphInstanceProvider
 * @returns GraphInstanceContextValue containing sigma, graph, and layout instances
 *
 * @example
 * ```tsx
 * const { sigma, graph } = useGraphInstanceContext();
 *
 * // Add node to graph
 * useEffect(() => {
 *   if (!graph) return;
 *
 *   graph.addNode('node1', {
 *     x: Math.random() * 100,
 *     y: Math.random() * 100,
 *     label: 'New Node',
 *     size: 10,
 *   });
 *
 *   sigma?.refresh();
 * }, [graph, sigma]);
 * ```
 */
export const useGraphInstanceContext = (): GraphInstanceContextValue => {
  const context = useContext(GraphInstanceContext);
  if (!context) {
    throw new Error(
      'useGraphInstanceContext must be used within GraphInstanceProvider'
    );
  }
  return context;
};
